import { MasterProcedureStep } from "../models/master-procedure-step.model";

export function validateMasterProcedureGraph(steps: MasterProcedureStep[]): string[] {
    const errors: string[] = [];
    if (!steps.length) {
        errors.push('add at least one procedure');
        return errors;
    }

    const stepIds = new Set(steps.map(step => step.id));
    for (const step of steps) {
        if (!step.procedureId) {
            errors.push('every node must link to a procedure');
        }
        const productKeys = new Set<string>();
        for (const product of step.products) {
            if (!product.source || !product.productId) {
                errors.push('every linked product must include a source and product');
            }
            const productKey = `${product.source}:${product.productId}`;
            if (productKeys.has(productKey)) errors.push('a product can only be linked once per node');
            productKeys.add(productKey);
        }
        if (step.nextStepId === step.id) {
            errors.push('a node cannot link to itself');
        } else if (step.nextStepId && !stepIds.has(step.nextStepId)) {
            errors.push('a node links outside this master procedure version');
        }
    }

    const state = new Map<string, 'visiting' | 'visited'>();
    const byId = new Map(steps.map(step => [step.id, step]));
    const visit = (stepId: string): boolean => {
        if (state.get(stepId) === 'visiting') return true;
        if (state.get(stepId) === 'visited') return false;
        state.set(stepId, 'visiting');
        const nextStepId = byId.get(stepId)?.nextStepId;
        if (nextStepId && visit(nextStepId)) return true;
        state.set(stepId, 'visited');
        return false;
    };

    if (steps.some(step => visit(step.id))) {
        errors.push('the flowchart cannot contain a cycle');
    }

    return [...new Set(errors)];
}

export function arrangeMasterProcedureSteps(steps: MasterProcedureStep[]): MasterProcedureStep[] {
    if (!steps.length) return [];

    const byId = new Map(steps.map(step => [step.id, step]));
    const predecessors = new Map(steps.map(step => [step.id, [] as string[]]));
    for (const step of steps) {
        if (step.nextStepId && predecessors.has(step.nextStepId)) {
            predecessors.get(step.nextStepId)!.push(step.id);
        }
    }

    const downstreamById = new Map<string, { distance: number; terminalId: string }>();
    const visiting = new Set<string>();
    const resolveDownstream = (id: string): { distance: number; terminalId: string } => {
        const existing = downstreamById.get(id);
        if (existing) return existing;
        if (visiting.has(id)) return { distance: 0, terminalId: id };

        visiting.add(id);
        const nextId = byId.get(id)?.nextStepId;
        const downstream = nextId && byId.has(nextId)
            ? resolveDownstream(nextId)
            : { distance: -1, terminalId: id };
        const resolved = {
            distance: downstream.distance + 1,
            terminalId: downstream.terminalId,
        };
        visiting.delete(id);
        downstreamById.set(id, resolved);
        return resolved;
    };

    steps.forEach(step => resolveDownstream(step.id));

    const maxDistanceByTerminal = new Map<string, number>();
    for (const { distance, terminalId } of downstreamById.values()) {
        maxDistanceByTerminal.set(
            terminalId,
            Math.max(maxDistanceByTerminal.get(terminalId) ?? 0, distance),
        );
    }

    const columns = new Map<number, string[]>();
    for (const step of steps) {
        const downstream = downstreamById.get(step.id)!;
        const column = (maxDistanceByTerminal.get(downstream.terminalId) ?? downstream.distance) - downstream.distance;
        columns.set(column, [...(columns.get(column) ?? []), step.id]);
    }

    const nodeHeight = (id: string): number => 110 + byId.get(id)!.products.length * 48;
    const upstreamSizeById = new Map<string, number>();
    const upstreamSize = (id: string): number => {
        const existing = upstreamSizeById.get(id);
        if (existing !== undefined) return existing;
        const size = (predecessors.get(id) ?? []).reduce((total, predecessorId) => total + upstreamSize(predecessorId) + 1, 0);
        upstreamSizeById.set(id, size);
        return size;
    };
    steps.forEach(step => upstreamSize(step.id));

    const positions = new Map<string, { x: number; y: number }>();
    const verticalGap = 80;
    [...columns.entries()].sort(([a], [b]) => a - b).forEach(([column, ids]) => {
        const groups = new Map<string, string[]>();
        ids.forEach(id => {
            const groupKey = byId.get(id)!.nextStepId || `terminal:${id}`;
            groups.set(groupKey, [...(groups.get(groupKey) ?? []), id]);
        });

        const blocks = [...groups.values()].map(groupIds => {
            const orderedByOriginalY = [...groupIds].sort((a, b) => byId.get(a)!.y - byId.get(b)!.y);
            const connectedIds = orderedByOriginalY.filter(id => (predecessors.get(id)?.length ?? 0) > 0);
            const primaryId = [...connectedIds].sort((a, b) => {
                const sizeDifference = upstreamSize(b) - upstreamSize(a);
                return sizeDifference || byId.get(a)!.y - byId.get(b)!.y;
            })[0];
            const orderedIds = [...orderedByOriginalY];

            if (primaryId && orderedIds.length > 1) {
                orderedIds.splice(orderedIds.indexOf(primaryId), 1);
                orderedIds.splice(Math.floor((groupIds.length - 1) / 2), 0, primaryId);
            }

            const offsets = new Map<string, number>();
            let blockHeight = 0;
            orderedIds.forEach((id, index) => {
                offsets.set(id, blockHeight);
                blockHeight += nodeHeight(id);
                if (index < orderedIds.length - 1) blockHeight += verticalGap;
            });

            const desiredY = (id: string): number | null => {
                const positionedPredecessors = (predecessors.get(id) ?? []).filter(predecessorId => positions.has(predecessorId));
                if (!positionedPredecessors.length) return null;
                const averageCenter = positionedPredecessors.reduce((total, predecessorId) => {
                    return total + positions.get(predecessorId)!.y + nodeHeight(predecessorId) / 2;
                }, 0) / positionedPredecessors.length;
                return averageCenter - nodeHeight(id) / 2;
            };

            const primaryDesiredY = primaryId ? desiredY(primaryId) : null;
            const positionedDesiredTops = orderedIds
                .map(id => {
                    const itemDesiredY = desiredY(id);
                    return itemDesiredY === null ? null : itemDesiredY - offsets.get(id)!;
                })
                .filter((value): value is number => value !== null);
            const desiredTop = primaryId && primaryDesiredY !== null
                ? primaryDesiredY - offsets.get(primaryId)!
                : positionedDesiredTops.length
                    ? positionedDesiredTops.reduce((total, value) => total + value, 0) / positionedDesiredTops.length
                    : Math.min(...orderedIds.map(id => byId.get(id)!.y));

            return { blockHeight, desiredTop, offsets, orderedIds };
        }).sort((a, b) => a.desiredTop - b.desiredTop);

        let nextAvailableY = Number.NEGATIVE_INFINITY;
        blocks.forEach(block => {
            const blockTop = Math.max(block.desiredTop, nextAvailableY);
            block.orderedIds.forEach(id => {
                positions.set(id, {
                    x: column * 340,
                    y: blockTop + block.offsets.get(id)!,
                });
            });
            nextAvailableY = blockTop + block.blockHeight + verticalGap;
        });
    });

    const minimumY = Math.min(...Array.from(positions.values(), position => position.y));
    return steps.map(step => {
        const position = positions.get(step.id)!;
        return { ...step, x: position.x, y: position.y - minimumY };
    });
}

export function cloneMasterProcedureSteps(
    steps: MasterProcedureStep[],
    createId: () => string,
): MasterProcedureStep[] {
    const idMap = new Map(steps.map(step => [step.id, createId()]));
    return steps.map(step => ({
        ...step,
        id: idMap.get(step.id)!,
        products: step.products.map(product => ({ ...product })),
        nextStepId: step.nextStepId ? idMap.get(step.nextStepId) ?? null : null,
    }));
}
